import { FC, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
} from '@mui/material';
import {
  IoExpandOutline,
  IoPeopleOutline,
  IoWarningOutline,
} from 'react-icons/io5';
import {
  MergedReviewRow,
  MergedSummaryRow,
  ReviewCell,
} from './costCenterReview.types';
import PeriodTable from './PeriodTable';
import SignedAmount from './SignedAmount';
import {
  accountRowSx,
  fteFormat,
  useFormatAmount,
  stickySx,
  tableSx,
  valueCellSx,
} from './reviewTableStyles';
import { PeriodStatus, describeRow, rollUpTables } from './reviewDerived';

interface CostCenterReviewDetailsProps {
  metaLoading: boolean;
  periods: string[];
  periodStatus: PeriodStatus[];
  rows: MergedReviewRow[];
  byAccountingType: MergedSummaryRow[] | null;
  accountAttributeLabel?: string | null;
  byPayGroup: MergedSummaryRow[];
  noData: boolean;
  // Opens the "Expandera" modal for a clicked amount cell
  onExpand?: (cell: ReviewCell) => void;
  // Opens "Expandera per anställd" for a clicked amount cell
  onExpandPerEmployee?: (cell: ReviewCell) => void;
  // Opens the FTE drilldown for a clicked period's FTE figure
  onExpandFte?: (period: string) => void;
}

// The "Detaljer" tab: the account/paycode table and its roll-ups.
const CostCenterReviewDetails: FC<CostCenterReviewDetailsProps> = ({
  metaLoading,
  periods,
  periodStatus,
  rows,
  byAccountingType,
  accountAttributeLabel = null,
  byPayGroup,
  noData,
  onExpand,
  onExpandPerEmployee,
  onExpandFte,
}) => {
  const formatAmount = useFormatAmount();
  const { t } = useTranslation();
  const [menu, setMenu] = useState<{
    anchor: HTMLElement;
    cell: ReviewCell;
  } | null>(null);

  const openMenu = (
    event: MouseEvent<HTMLElement>,
    row: MergedReviewRow,
    period: string
  ) => setMenu({ anchor: event.currentTarget, cell: { row, period } });

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ maxHeight: 'calc(100vh - 310px)' }}
      >
        <Table stickyHeader size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={stickySx(0, true)}>{t('ACCOUNT')}</TableCell>
              <TableCell sx={stickySx(1, true)}>{t('DESCRIPTION')}</TableCell>
              <TableCell sx={stickySx(2, true)}>{t('PAY_GROUP')}</TableCell>
              {metaLoading
                ? Array.from({ length: 12 }).map((_, i) => (
                    <TableCell key={i} align="right">
                      <Skeleton width={60} sx={{ ml: 'auto' }} />
                    </TableCell>
                  ))
                : periodStatus.map(({ period, error }) => (
                    <TableCell
                      key={period}
                      align="right"
                      sx={{ minWidth: 110 }}
                    >
                      {error ? (
                        <Tooltip title={t('PERIOD_LOAD_ERROR')}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'error.main',
                            }}
                          >
                            <IoWarningOutline />
                            {period}
                          </Box>
                        </Tooltip>
                      ) : (
                        period
                      )}
                    </TableCell>
                  ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={stickySx(0)} />
              <TableCell
                sx={[stickySx(1), { fontWeight: 'bold' }] as SxProps<Theme>}
                align="right"
              >
                {t('FTE')}
              </TableCell>
              <TableCell sx={stickySx(2)} />
              {periodStatus.map(({ period, loading, fte }) => {
                const clickable =
                  Boolean(onExpandFte) && !loading && typeof fte === 'number';
                return (
                  <TableCell
                    key={period}
                    align="right"
                    sx={
                      [
                        valueCellSx,
                        { fontWeight: 'bold' },
                        clickable && {
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        },
                      ] as SxProps<Theme>
                    }
                    onClick={
                      clickable ? () => onExpandFte?.(period) : undefined
                    }
                  >
                    {loading ? (
                      <Skeleton width={30} sx={{ ml: 'auto' }} />
                    ) : fte === null ? (
                      '–'
                    ) : (
                      fteFormat.format(fte)
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
            {rows.map((row) => {
              const bold = row.kind === 'account';
              const cellSx = { fontWeight: bold ? 'bold' : undefined };
              return (
                <TableRow
                  key={row.key}
                  hover
                  sx={bold ? accountRowSx : undefined}
                >
                  <TableCell sx={[stickySx(0), cellSx] as SxProps<Theme>}>
                    {row.account}
                  </TableCell>
                  <TableCell
                    sx={[stickySx(1), cellSx] as SxProps<Theme>}
                    title={describeRow(row)}
                  >
                    {describeRow(row)}
                  </TableCell>
                  <TableCell sx={stickySx(2)}>{row.payGroup ?? ''}</TableCell>
                  {periodStatus.map(({ period, loading }) => {
                    const clickable =
                      Boolean(onExpand) &&
                      !loading &&
                      row.amounts[period] !== undefined;
                    return (
                      <TableCell
                        key={period}
                        align="right"
                        sx={
                          [
                            valueCellSx,
                            cellSx,
                            clickable && {
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            },
                          ] as SxProps<Theme>
                        }
                        onClick={
                          clickable
                            ? (event) => openMenu(event, row, period)
                            : undefined
                        }
                      >
                        {loading ? (
                          <Skeleton width={50} sx={{ ml: 'auto' }} />
                        ) : (
                          <SignedAmount
                            value={row.amounts[period]}
                            format={formatAmount}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            {!rows.length &&
              !noData &&
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={3 + Math.max(periods.length, 12)}>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (menu) {
              onExpand?.(menu.cell);
            }
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <IoExpandOutline />
          </ListItemIcon>
          <ListItemText>{t('REVIEW_EXPAND')}</ListItemText>
        </MenuItem>
        {onExpandPerEmployee && (
          <MenuItem
            onClick={() => {
              if (menu) {
                onExpandPerEmployee(menu.cell);
              }
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <IoPeopleOutline />
            </ListItemIcon>
            <ListItemText>{t('REVIEW_EXPAND_PER_EMPLOYEE')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {rows.length > 0 &&
        rollUpTables(
          rows,
          periodStatus,
          byAccountingType,
          byPayGroup,
          t as (key: string, options?: Record<string, unknown>) => string,
          accountAttributeLabel
        ).map((table) => (
          <PeriodTable
            key={table.key}
            title={table.title}
            periods={periodStatus}
            rows={table.rows}
          />
        ))}
    </>
  );
};

export default CostCenterReviewDetails;
